import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';

type ToastSeverity = 'success' | 'info' | 'warn' | 'error';

const SEVERITY_LABEL_KEYS: Record<ToastSeverity, string> = {
	success: 'common.labels.success',
	info: 'common.labels.info',
	warn: 'common.labels.warning',
	error: 'common.labels.error',
};

@Injectable({
	providedIn: 'root', // Available across the whole app
})
export class MessageAlertService {
	private readonly messageService = inject(MessageService);
	private readonly transloco = inject(TranslocoService);

	/**
	 * Displays a toast message using PrimeNG MessageService.
	 * @param severity Type of message ('success', 'info', 'warn', 'error').
	 * @param summary Short title. Pass `null` to derive it from the severity
	 *   via i18n (common.labels.*) instead of hardcoding it at the call site.
	 * @param detail Detailed message.
	 * @param lifeMs Optional time in ms before auto-dismiss (default PrimeNG ~3000).
	 *   Use longer (8000-10000) for notifications the user must definitely see.
	 */
	showMessage(severity: ToastSeverity, summary: string | null, detail: string, lifeMs?: number): void {
		this.messageService.add({
			severity,
			summary: summary ?? this.transloco.translate(SEVERITY_LABEL_KEYS[severity]),
			detail,
			...(lifeMs && { life: lifeMs }),
		});
	}

	/**
	 * Displays a toast message with a specific key (for multiple toast instances with different styles).
	 * @param severity Type of message ('success', 'info', 'warn', 'error').
	 * @param summary Short title. `null` derives it from the severity (i18n).
	 * @param detail Detailed message.
	 * @param key Unique key to identify which toast container to use.
	 */
	showMessageWithKey(severity: ToastSeverity, summary: string | null, detail: string, key: string): void {
		this.messageService.add({
			severity,
			summary: summary ?? this.transloco.translate(SEVERITY_LABEL_KEYS[severity]),
			detail,
			key,
		});
	}
}
